/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
// from https://gist.github.com/ca0v/73a31f57b397606c9813472f7493a940
// eslint-disable-next-line @typescript-eslint/ban-types
export const debounce = (cb, wait = 20) => {
    let timer;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callable = (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => cb(...args), wait);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return callable;
};
export function debounceHandler(handler, timeout) {
    const debounced = debounce(handler, timeout);
    return event => {
        event.persist();
        return debounced(event);
    };
}
export const classNames = (...args) => {
    return args.reduce((acc, cur) => {
        if (Array.isArray(cur)) {
            const className = classNames(...cur);
            if (!className)
                return acc;
            return `${acc} ${className}`;
        }
        if (cur)
            return `${acc} ${cur}`;
        return acc;
    }, '');
};
export const responsiveVariables = (variableName, value) => {
    if (value === undefined)
        return {};
    if (typeof value !== 'object')
        return { [variableName]: value };
    return Object.keys(value).reduce((acc, key) => {
        const isBase = key === 's';
        const suffix = isBase ? '' : `-${key}`;
        const viewportValue = value[key];
        if (viewportValue === undefined)
            return acc;
        if (viewportValue === false)
            return acc;
        return Object.assign(Object.assign({}, acc), { [`${variableName}${suffix}`]: viewportValue });
    }, {});
};
const isResponsive = (prop) => {
    if (prop === null)
        return false;
    return typeof prop === 'object' && prop !== null && 's' in prop;
};
export const responsivePropDependency = (prop, resolver) => {
    if (!isResponsive(prop))
        return resolver(prop, 's');
    const keys = Object.keys(prop);
    return keys.reduce((acc, viewport) => {
        const viewportValue = prop[viewport];
        if (viewportValue === undefined || viewportValue === null)
            return acc;
        return Object.assign(Object.assign({}, acc), { [viewport]: resolver(viewportValue, viewport) });
    }, {});
};
const applyClassName = (className, value, base) => {
    if (value === true && base)
        return className;
    // CSS should be turned on/off for non base viewport with mobile first approach
    if (value === true && !base)
        return `${className}-true`;
    if (value === false && !base)
        return `${className}-false`;
    if (value !== undefined)
        return `${className}-${value}`;
    return null;
};
export const responsiveClassNames = (s, className, value) => {
    if (typeof value !== 'object') {
        const staticClassName = applyClassName(className, value, true);
        return staticClassName ? [s[staticClassName]] : [];
    }
    return Object.keys(value).reduce((acc, key) => {
        const isBase = key === 's';
        const viewportClassName = applyClassName(className, value[key], isBase);
        const suffix = isBase ? '' : `--${key}`;
        return [...acc, s[`${viewportClassName}${suffix}`]];
    }, []);
};
// This can be used to help narrow down a string's type (which cannot be done with Array.prototype.includes)
export const narrowString = (string, narrower) => narrower.find(narrowerString => narrowerString === string);
export const isEdge16 = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('edge/16.');
};
export const urlSearchParamsPolyfill = (params) => {
    const searchParams = [];
    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            const encodedKey = encodeURIComponent(key);
            const encodedValue = encodeURIComponent(params[key]);
            searchParams.push(`${encodedKey}=${encodedValue}`);
        }
    }
    return searchParams.join('&');
};
export const createUrlSearchParams = (params) => {
    if (isEdge16()) {
        // Use the polyfill for Edge 16
        return urlSearchParamsPolyfill(params);
    }
    else {
        // Use the native URLSearchParams for other browsers
        return new URLSearchParams(params).toString();
    }
};
export const getUrlSearchParams = () => {
    if (isEdge16()) {
        // Use the polyfill for Edge 16
        const searchString = window.location.search.slice(1); // Remove the leading '?'
        const params = {};
        searchString.split('&').forEach(pair => {
            const [key, value] = pair.split('=');
            if (key) {
                params[decodeURIComponent(key)] = decodeURIComponent(value || '');
            }
        });
        // Create a URLSearchParams-like object using the polyfill
        return {
            get: (key) => params[key] || null,
            getAll: (key) => (params[key] ? [params[key]] : []),
            has: (key) => key in params,
            keys: () => Object.keys(params)[Symbol.iterator](),
            values: () => Object.values(params)[Symbol.iterator](),
            entries: () => Object.entries(params)[Symbol.iterator](),
            forEach: (callback) => {
                Object.entries(params).forEach(([key, value]) => {
                    callback(value, key);
                });
            },
            toString: () => urlSearchParamsPolyfill(params),
            // Add other methods as needed to match URLSearchParams interface
        };
    }
    else {
        // Use the native URLSearchParams for other browsers
        return new URLSearchParams(window.location.search);
    }
};
